// src/pages/seeker/SeekerCVEditor.jsx
// CV Mockup editor — two-column CV layout with modal editing
// Route: /seeker/profile/cv/:id

import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { seekerAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { X, Check, Camera, ArrowLeft, Phone, MapPin, Calendar, Atom, Briefcase, Clock, ListChecks, Building2, Mail } from 'lucide-react';
import { DelBtn } from '../../components/shared/UI';

// ── Palette ────────────────────────────────────────────
const C = {
  navy:     '#0e4e95',
  blue:     '#3D80CC',
  white:    '#ffffff',
  offwhite: '#f4f7fb',
  text:     '#1a1a1a',
  muted:    '#666',
  border:   '#d0daea',
  green:    '#28A745',
  red:      '#CC0000',
};
const font = "'Exo 2', sans-serif";
const fmtEnum = (v) => v ? v.replace(/_/g, ' ') : '';

// ── Modal shell ────────────────────────────────────────
const Modal = ({ title, onClose, onSave, saving, children, wide }) => (
  <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
    <div style={{ background: C.white, borderRadius: '6px', width: '100%', maxWidth: wide ? '680px' : '560px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', fontFamily: font }}>
      <div style={{ background: C.navy, color: C.white, padding: '14px 20px', borderRadius: '6px 6px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <span style={{ fontWeight: 700, fontSize: '15px' }}>{title}</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', display: 'flex' }}><X size={18} /></button>
      </div>
      <div style={{ overflowY: 'auto', padding: '20px', flex: 1 }}>{children}</div>
      <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: '8px', justifyContent: 'flex-end', flexShrink: 0 }}>
        <button onClick={onClose} disabled={saving} className="btn btn-danger" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><X size={13} /> Cancel</button>
        <button onClick={onSave} disabled={saving} className="btn btn-success" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><Check size={13} /> {saving ? 'Saving…' : 'Save'}</button>
      </div>
    </div>
  </div>
);

// ── Form primitives ────────────────────────────────────
const FL = ({ label, children, hint }) => (
  <div style={{ marginBottom: '14px' }}>
    <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: C.blue, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>{label}</label>
    {children}
    {hint && <div style={{ fontSize: '11px', color: C.muted, marginTop: '3px' }}>{hint}</div>}
  </div>
);
const inputBase = { width: '100%', padding: '7px 10px', fontSize: '13px', border: `1px solid ${C.border}`, borderRadius: '3px', fontFamily: font, color: C.text, boxSizing: 'border-box', outline: 'none' };
const FI = ({ value, onChange, placeholder, type = 'text', maxLength, rows, onKeyDown }) => {
  if (rows) return <textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} maxLength={maxLength} rows={rows} style={{ ...inputBase, resize: 'vertical' }} />;
  return <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} onKeyDown={onKeyDown} placeholder={placeholder} maxLength={maxLength} style={inputBase} />;
};
const FS = ({ value, onChange, options }) => (
  <select value={value || ''} onChange={e => onChange(e.target.value || null)} style={{ ...inputBase, background: C.white }}>
    <option value="">— Select —</option>
    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

// ── Editable zone ──────────────────────────────────────
const Editable = ({ onClick, children, style = {} }) => {
  const [hover, setHover] = useState(false);
  return (
    <div onClick={onClick} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ position: 'relative', cursor: 'pointer', borderRadius: '4px', outline: hover ? `2px dashed ${C.blue}` : '2px dashed transparent', transition: 'outline 0.1s', ...style }}>
      {hover && <span style={{ position: 'absolute', top: '-11px', right: 0, zIndex: 10, background: C.blue, color: C.white, fontSize: '9px', fontWeight: 700, padding: '1px 6px', borderRadius: '2px', pointerEvents: 'none', fontFamily: font, letterSpacing: '0.06em' }}>EDIT</span>}
      {children}
    </div>
  );
};

// ── CV headings ────────────────────────────────────────
const SideHead = ({ label }) => (
  <div style={{ marginBottom: '10px', paddingBottom: '6px', borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
    <span style={{ fontSize: '13px', fontWeight: 800, color: C.white, textTransform: 'uppercase', letterSpacing: '0.08em' }}>➤ {label}</span>
  </div>
);
const MainHead = ({ label }) => (
  <div style={{ marginBottom: '14px', paddingBottom: '6px', borderBottom: `2px solid ${C.navy}` }}>
    <span style={{ fontSize: '17px', fontWeight: 800, color: C.navy, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
  </div>
);
const ComingSoon = ({ label }) => (
  <div style={{ padding: '14px 16px', background: C.offwhite, borderRadius: '4px', border: `1px dashed ${C.border}`, textAlign: 'center' }}>
    <p style={{ fontSize: '12px', color: C.muted, margin: '0 0 4px' }}>{label} — coming soon</p>
    <span style={{ fontSize: '11px', color: C.blue }}>Will be editable here in a future update</span>
  </div>
);

// ══════════════════════════════════════════════════════
// MODAL COMPONENTS
// ══════════════════════════════════════════════════════

// Headline only (name is read-only)
const HeadlineModal = ({ p, onClose, onSave, saving }) => {
  const [form, setF] = useState({ headline: p.headline || '' });
  return (
    <Modal title="Headline" onClose={onClose} onSave={() => onSave(form)} saving={saving}>
      <p style={{ fontSize: '13px', color: C.muted, margin: '0 0 14px' }}>
        Your name is managed in your account settings. You can edit your professional headline here.
      </p>
      <FL label="Headline / Job Title" hint="e.g. Senior React Developer — max 120 characters">
        <FI value={form.headline} onChange={v => setF(f => ({ ...f, headline: v }))} maxLength={120} placeholder="e.g. Accounting & Bookkeeping Officer" />
      </FL>
    </Modal>
  );
};

const ContactModal = ({ contacts, cvContacts, residence, onClose, onSave, saving }) => {
  const [selected, setSelected] = useState(() => new Set(cvContacts || []));
  const [res, setRes] = useState({
    street:  residence?.street  ?? false,
    city:    residence?.city    ?? true,
    country: residence?.country ?? true,
  });

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleRes = (key) => setRes(r => ({ ...r, [key]: !r[key] }));

  const saveData = async () => ({ cvContacts: [...selected], residence: res });

  const phones = contacts.filter(c => c.type === 'PHONE');
  const emails = contacts.filter(c => c.type === 'EMAIL');
  const links  = contacts.filter(c => c.type === 'URL');

  const iconStyle = { fontSize: '14px' };
  const typeIcon  = (type) => <span style={iconStyle}>{type === 'PHONE' ? '📞' : type === 'EMAIL' ? '✉' : '🔗'}</span>;

  const renderGroup = (label, items) => items.length === 0 ? null : (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ fontSize: '11px', fontWeight: 700, color: C.blue, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>{label}</div>
      {items.map(c => {
        const checked = selected.has(c.id);
        return (
          <div key={c.id} onClick={() => toggle(c.id)}
            style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', marginBottom: '4px', borderRadius: '4px', border: `1.5px solid ${checked ? C.navy : C.border}`, background: checked ? '#e8eef5' : C.white, cursor: 'pointer' }}>
            <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: checked ? C.navy : 'transparent', border: `1.5px solid ${checked ? C.navy : C.muted}`, flexShrink: 0 }} />
            {typeIcon(c.type)}
            <span style={{ flex: 1, fontSize: '13px', fontWeight: 600, color: checked ? C.navy : C.text }}>{c.value}</span>
            {c.label && <span style={{ fontSize: '11px', color: C.muted }}>{c.label}</span>}
            {c.isPrimary && <span style={{ fontSize: '10px', color: '#e6a817' }}>★</span>}
          </div>
        );
      })}
    </div>
  );

  const ResToggle = ({ label, field }) => {
    const on = res[field];
    return (
      <div onClick={() => toggleRes(field)}
        style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', marginBottom: '4px', borderRadius: '4px', border: `1.5px solid ${on ? C.navy : C.border}`, background: on ? '#e8eef5' : C.white, cursor: 'pointer' }}>
        <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: on ? C.navy : 'transparent', border: `1.5px solid ${on ? C.navy : C.muted}`, flexShrink: 0 }} />
        <span style={{ fontSize: '13px', fontWeight: 600, color: on ? C.navy : C.text }}>{label}</span>
      </div>
    );
  };

  return (
    <Modal title="Contacts" onClose={onClose} onSave={() => onSave(saveData)} saving={saving}>
      <p style={{ fontSize: '12px', color: C.muted, margin: '0 0 14px' }}>
        Tick the contacts to include in this CV.
      </p>

      {renderGroup('Phone Numbers', phones)}
      {renderGroup('Email Addresses', emails)}
      {renderGroup('Links', links)}
      {contacts.length === 0 && <p style={{ fontSize: '13px', color: C.muted, marginBottom: '14px' }}>No contacts in your profile yet. Visit the Personal Details page to add contacts.</p>}

      {/* Residence */}
      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '14px', marginTop: '4px' }}>
        <div style={{ fontSize: '11px', fontWeight: 700, color: C.blue, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Residence</div>
        <ResToggle label="Street Address" field="street" />
        <ResToggle label="City"           field="city" />
        <ResToggle label="Country"        field="country" />
      </div>
    </Modal>
  );
};

const SummaryModal = ({ p, onClose, onSave, saving }) => {
  const [form, setF] = useState({ summary: p.summary || '' });
  return (
    <Modal title="Summary / Bio" onClose={onClose} onSave={() => onSave(form)} saving={saving}>
      <FL label="Summary" hint="Max 2000 characters">
        <FI value={form.summary} onChange={v => setF(f => ({ ...f, summary: v }))} rows={8} maxLength={2000} placeholder="A short professional summary…" />
      </FL>
    </Modal>
  );
};

const PersonalModal = ({ p, onClose, onSave, saving }) => {
  const [form, setF] = useState({ gender: p.gender || '', dateOfBirth: p.dateOfBirth ? p.dateOfBirth.slice(0, 10) : '', workRightStatus: p.workRightStatus || '', availabilityStatus: p.availabilityStatus || '', workPreference: p.workPreference || '', linkedinUrl: p.linkedinUrl || '', portfolioUrl: p.portfolioUrl || '' });
  return (
    <Modal title="Personal & Work Preferences" onClose={onClose} onSave={() => onSave(form)} saving={saving}>
      <div className="form-row">
        <FL label="Gender"><FS value={form.gender} onChange={v => setF(f => ({ ...f, gender: v }))} options={[{ value: 'MALE', label: 'Male' }, { value: 'FEMALE', label: 'Female' }, { value: 'ALTERNATIVE', label: 'Alternative' }, { value: 'PREFER_NOT_TO_SAY', label: 'Prefer not to say' }]} /></FL>
        <FL label="Date of Birth"><FI value={form.dateOfBirth} onChange={v => setF(f => ({ ...f, dateOfBirth: v }))} type="date" /></FL>
        <FL label="Work Rights"><FS value={form.workRightStatus} onChange={v => setF(f => ({ ...f, workRightStatus: v }))} options={[{ value: 'CITIZEN', label: 'Citizen' }, { value: 'PERMANENT_RESIDENT', label: 'Permanent Resident' }, { value: 'WORK_VISA', label: 'Work Visa' }, { value: 'STUDENT_VISA', label: 'Student Visa' }, { value: 'REQUIRES_SPONSORSHIP', label: 'Requires Sponsorship' }]} /></FL>
        <FL label="Availability"><FS value={form.availabilityStatus} onChange={v => setF(f => ({ ...f, availabilityStatus: v }))} options={[{ value: 'IMMEDIATELY', label: 'Immediately' }, { value: 'TWO_WEEKS', label: 'Two Weeks' }, { value: 'ONE_MONTH', label: 'One Month' }, { value: 'THREE_MONTHS', label: 'Three Months' }, { value: 'NOT_LOOKING', label: 'Not Looking' }]} /></FL>
        <FL label="Work Preference"><FS value={form.workPreference} onChange={v => setF(f => ({ ...f, workPreference: v }))} options={[{ value: 'ONSITE', label: 'On-site' }, { value: 'REMOTE', label: 'Remote' }, { value: 'HYBRID', label: 'Hybrid' }, { value: 'FLEXIBLE', label: 'Flexible' }]} /></FL>
      </div>
      <FL label="LinkedIn URL"><FI value={form.linkedinUrl} onChange={v => setF(f => ({ ...f, linkedinUrl: v }))} placeholder="https://linkedin.com/in/…" /></FL>
      <FL label="Portfolio URL"><FI value={form.portfolioUrl} onChange={v => setF(f => ({ ...f, portfolioUrl: v }))} placeholder="https://…" /></FL>
    </Modal>
  );
};

// Skills modal for CV — checklist from profile sections, per-section selection
const SkillsModal = ({ skillSections, cvSkills, onClose, onSave, saving }) => {
  // cvSkills is { sectionTitle: [skillName, ...] }
  const [selected, setSelected] = useState(() => {
    const m = {};
    for (const sec of (skillSections || [])) {
      m[sec.title] = new Set((cvSkills && cvSkills[sec.title]) || []);
    }
    return m;
  });

  const toggle = (sectionTitle, name) => {
    setSelected(prev => {
      const secSet = new Set(prev[sectionTitle] || []);
      secSet.has(name) ? secSet.delete(name) : secSet.add(name);
      return { ...prev, [sectionTitle]: secSet };
    });
  };

  const saveSkills = async () => {
    // Build section-keyed object
    const result = {};
    for (const [title, set] of Object.entries(selected)) {
      if (set.size > 0) result[title] = [...set];
    }
    return { cvSkills: result };
  };

  const sections = (skillSections || []).filter(s => s.items.length > 0);

  return (
    <Modal title="Skills & Knowledge" onClose={onClose} onSave={() => onSave(saveSkills)} saving={saving} wide>
      <p style={{ fontSize: '12px', color: C.muted, margin: '0 0 14px' }}>
        Tick the skills to include in this CV. Skills are selected per section.
      </p>

      {sections.map((sec, si) => (
        <div key={si} style={{ marginBottom: '16px' }}>
          {sec.title && <div style={{ fontSize: '11px', fontWeight: 700, color: C.blue, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>{sec.title}</div>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {sec.items.map((item, ii) => {
              const checked = selected[sec.title]?.has(item);
              return (
                <button key={ii} onClick={() => toggle(sec.title, item)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '20px', border: `1.5px solid ${checked ? C.navy : C.border}`, background: checked ? '#e8eef5' : C.white, color: checked ? C.navy : C.muted, cursor: 'pointer', fontFamily: font, transition: 'all 0.1s' }}>
                  <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: checked ? C.navy : 'transparent', border: `1.5px solid ${checked ? C.navy : C.muted}`, flexShrink: 0, display: 'inline-block' }} />
                  {item}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {sections.length === 0 && (
        <p style={{ fontSize: '13px', color: C.muted, marginBottom: '14px' }}>No skills in your profile yet. Add skills on the Skills page first.</p>
      )}
    </Modal>
  );
};

// Languages modal for CV — checklist from profile, add new syncs to profile
const LanguagesModal = ({ languages, cvLanguages, onClose, onSave, saving }) => {
  // languages: full profile list [{ name, level }]
  // cvLanguages: selected language names for this CV
  const [selected, setSelected]     = useState(() => new Set(cvLanguages || []));
  const [newName, setNewName]       = useState('');
  const [newLevel, setNewLevel]     = useState('');
  const [addedToProfile, setAddedToProfile] = useState([]); // new entries queued for profile save

  const LEVELS = ['Native', 'Fluent', 'Advanced', 'Intermediate', 'Basic'];

  const toggle = (name) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const addNew = () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    const exists = languages.some(l => l.name.toLowerCase() === trimmed.toLowerCase());
    if (!exists) {
      setAddedToProfile(prev => [...prev, { name: trimmed, level: newLevel || 'Conversational' }]);
    }
    setSelected(prev => new Set([...prev, trimmed]));
    setNewName(''); setNewLevel('');
  };

  const saveData = async () => {
    // 1. If new languages added, save each to profile via the proper API
    if (addedToProfile.length > 0) {
      const existing = languages.map(l => l.name.toLowerCase());
      const trulyNew = addedToProfile.filter(l => !existing.includes(l.name.toLowerCase()));
      for (const lang of trulyNew) {
        try {
          await seekerAPI.addLanguage({ language: lang.name, proficiency: (lang.level || 'basic').toLowerCase() });
        } catch (_) { /* skip if duplicate */ }
      }
    }
    // 2. Return selected names for CV sectionConfig
    return { cvLanguages: [...selected] };
  };

  // All profile languages + any new ones added this session not yet in profile
  const allLanguages = [
    ...languages,
    ...addedToProfile.filter(n => !languages.some(l => l.name.toLowerCase() === n.name.toLowerCase())),
  ];

  return (
    <Modal title="Languages" onClose={onClose} onSave={() => onSave(saveData)} saving={saving}>
      <p style={{ fontSize: '12px', color: C.muted, margin: '0 0 14px' }}>
        Tick the languages to include in this CV. Adding a new language also saves it to your profile.
      </p>

      <div style={{ marginBottom: '16px' }}>
        {allLanguages.map((item, i) => {
          const checked = selected.has(item.name);
          const isNew   = addedToProfile.some(n => n.name.toLowerCase() === item.name.toLowerCase());
          return (
            <div key={i} onClick={() => toggle(item.name)}
              style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '9px 12px', marginBottom: '4px', borderRadius: '4px', border: `1.5px solid ${checked ? C.navy : C.border}`, background: checked ? '#e8eef5' : C.white, cursor: 'pointer' }}>
              <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: checked ? C.navy : 'transparent', border: `1.5px solid ${checked ? C.navy : C.muted}`, flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: '13px', fontWeight: 600, color: checked ? C.navy : C.text }}>{item.name}</span>
              <span style={{ fontSize: '12px', color: C.muted }}>{item.level}</span>
              {isNew && <span style={{ fontSize: '10px', fontWeight: 700, color: C.green, textTransform: 'uppercase' }}>New</span>}
            </div>
          );
        })}
        {allLanguages.length === 0 && <p style={{ fontSize: '13px', color: C.muted }}>No languages in your profile yet. Add one below.</p>}
      </div>

      {/* Add new */}
      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '14px' }}>
        <div style={{ fontSize: '11px', fontWeight: 700, color: C.blue, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Add a new language</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px', alignItems: 'end' }}>
          <FL label="Language" hint="">
            <FI value={newName} onChange={setNewName} placeholder="e.g. English" onKeyDown={e => e.key === 'Enter' && addNew()} />
          </FL>
          <FL label="Level">
            <FS value={newLevel} onChange={setNewLevel} options={LEVELS.map(l => ({ value: l, label: l }))} />
          </FL>
          <button onClick={addNew} style={{ marginBottom: '14px', padding: '7px 14px', background: C.navy, color: C.white, border: 'none', borderRadius: '3px', cursor: 'pointer', fontWeight: 600, fontSize: '13px', fontFamily: font, whiteSpace: 'nowrap' }}>+ Add</button>
        </div>
      </div>
    </Modal>
  );
};

// ── Helpers for education/experience formatting ────────────────────
const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const fmtEduDate = (year, month) => {
  if (!year) return null;
  if (!month) return String(year);
  return `${MONTHS_SHORT[parseInt(month) - 1]} ${year}`;
};

// Normalise any date string (full ISO or YYYY-MM or YYYY) to local midnight to avoid UTC shift
const normExpDate = (d) => {
  if (!d) return null;
  // Take only YYYY-MM (first 7 chars), pad to full date, treat as local time
  const ym = d.slice(0, 7);            // "2020-01"
  return new Date(ym + '-01T00:00:00');
};

const fmtExpDate = (dateStr) => {
  if (!dateStr) return null;
  const d = normExpDate(dateStr);
  if (!d || isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
};

const fmtExpDuration = (startDate, endDate, isCurrent) => {
  if (!startDate) return null;
  if (isCurrent) return 'Current';
  if (!endDate) return null;
  const s = normExpDate(startDate);
  const e = normExpDate(endDate);
  const months = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
  if (months <= 0) return null;
  const yrs = Math.floor(months / 12), rem = months % 12;
  return [yrs && `${yrs} yr${yrs !== 1 ? 's' : ''}`, rem && `${rem} mo`].filter(Boolean).join(' ');
};

const SENIORITY_LABEL = {
  UNSPECIFIED: 'Unspecified', VOLUNTEER: 'Volunteer', OFFICER: 'Officer',
  SUPERVISOR: 'Supervisor', ASSISTANT_MANAGER: 'Asst. Manager',
  MANAGER: 'Manager', SENIOR_MANAGER: 'Senior Manager', EXECUTIVE_MANAGER: 'Executive Manager',
};

// ── CV-only display cards (no edit controls) ───────────────────────
const CVEduCard = ({ edu }) => {
  const heading = [edu.degree, edu.fieldOfStudy].filter(Boolean).join(' — ');
  const enrolledStr  = fmtEduDate(edu.startYear,  edu.startMonth);
  const graduatedStr = fmtEduDate(edu.endYear,    edu.endMonth);
  const units = Array.isArray(edu.units) ? edu.units : [];
  return (
    <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '6px', overflow: 'hidden' }}>
      <div style={{ padding: '8px 12px', background: '#4A6FA5', borderRadius: '6px 6px 0 0' }}>
        <span style={{ color: '#E8EDF2', fontSize: '13px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {heading || <span style={{ fontStyle: 'italic', opacity: 0.6, fontWeight: 400 }}>Untitled</span>}
        </span>
      </div>
      <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {edu.institution && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <MapPin size={13} color={C.muted} style={{ flexShrink: 0 }} />
            <span style={{ fontSize: '13px', color: C.text }}>{edu.institution}</span>
          </div>
        )}
        {(enrolledStr || graduatedStr) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Calendar size={13} color={C.muted} style={{ flexShrink: 0 }} />
            <span style={{ fontSize: '13px', color: C.text }}>
              {enrolledStr && <span>Enrolled {enrolledStr}</span>}
              {enrolledStr && graduatedStr && <span style={{ color: C.muted, margin: '0 6px' }}>→</span>}
              {graduatedStr && <span>Graduated {graduatedStr}</span>}
            </span>
          </div>
        )}
        {units.length > 0 ? (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <Atom size={13} color={C.muted} style={{ flexShrink: 0, marginTop: '3px' }} />
            <ul style={{ margin: 0, paddingLeft: 16, listStyleType: 'disc' }}>
              {units.map((u, i) => (
                <li key={i} style={{ fontSize: 11, color: C.text, marginBottom: 2, lineHeight: 1.5 }}>{u}</li>
              ))}
            </ul>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Atom size={13} color={C.muted} style={{ flexShrink: 0 }} />
            <span style={{ fontSize: '12px', color: C.muted, fontStyle: 'italic' }}>No core units</span>
          </div>
        )}
      </div>
    </div>
  );
};

const CVExpCard = ({ exp }) => {
  const roles = Array.isArray(exp.roles) ? exp.roles : [];
  const startedStr  = fmtExpDate(exp.startDate);
  const duration    = fmtExpDuration(exp.startDate, exp.endDate, exp.isCurrent);
  return (
    <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '6px', overflow: 'hidden' }}>
      <div style={{ padding: '8px 12px', background: '#4A6FA5', borderRadius: '6px 6px 0 0' }}>
        <span style={{ color: '#E8EDF2', fontSize: '13px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {exp.jobTitle || <span style={{ fontStyle: 'italic', opacity: 0.6, fontWeight: 400, textTransform: 'none' }}>Untitled position</span>}
        </span>
      </div>
      <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <Briefcase size={13} color={C.muted} style={{ flexShrink: 0 }} />
          <span style={{ fontSize: '13px', fontWeight: 600, color: C.text }}>
            {exp.company || <span style={{ color: C.muted, fontStyle: 'italic' }}>No employer</span>}
          </span>
          {exp.adminLevel && exp.adminLevel !== 'UNSPECIFIED' && (
            <span style={{ fontSize: '10px', fontWeight: 600, padding: '1px 7px', borderRadius: '10px', background: '#f0edf8', color: '#5a3e9a', border: '1px solid #cec3e8', flexShrink: 0 }}>
              {SENIORITY_LABEL[exp.adminLevel] || exp.adminLevel}
            </span>
          )}
        </div>
        {exp.location && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <MapPin size={13} color={C.muted} style={{ flexShrink: 0 }} />
            <span style={{ fontSize: '12px', color: C.text }}>{exp.location}</span>
          </div>
        )}
        {startedStr && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Clock size={13} color={C.muted} style={{ flexShrink: 0 }} />
            <span style={{ fontSize: '12px', color: C.text }}>
              Started {startedStr}
              {duration && <span style={{ color: C.muted }}> · {duration}</span>}
            </span>
          </div>
        )}
        {roles.length > 0 && (
          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '8px', marginTop: '2px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '6px' }}>
              <ListChecks size={11} color={C.blue} />
              <span style={{ fontSize: '10px', fontWeight: 700, color: C.blue, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Core Roles</span>
            </div>
            {roles.map((role) => (
              <div key={role.id} style={{ marginBottom: '8px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: C.text, marginBottom: '3px' }}>
                  {role.title || <span style={{ color: C.muted, fontStyle: 'italic' }}>Untitled role</span>}
                </div>
                {role.tasks?.length > 0 && (
                  <ul style={{ margin: 0, paddingLeft: 16, listStyleType: 'disc' }}>
                    {role.tasks.map((task) => (
                      <li key={task.id} style={{ fontSize: 11, color: C.text, marginBottom: 2, lineHeight: 1.5 }}>{task.text}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const CVRefCard = ({ data: r }) => (
  <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '6px', overflow: 'hidden' }}>
    <div style={{ padding: '8px 12px', background: '#4A6FA5', borderRadius: '6px 6px 0 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span style={{ color: '#E8EDF2', fontSize: '13px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {r.name || <span style={{ fontStyle: 'italic', opacity: 0.6, fontWeight: 400, textTransform: 'none' }}>Unnamed</span>}
      </span>
      {r.email && <span title={r.email} style={{ flexShrink: 0 }}><Mail size={13} color="rgba(255,255,255,0.75)" /></span>}
      {r.phone && <span title={[r.phone, r.phoneLabel].filter(Boolean).join(' — ')} style={{ flexShrink: 0 }}><Phone size={13} color="rgba(255,255,255,0.75)" /></span>}
    </div>
    <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {r.jobTitle && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Briefcase size={13} color={C.muted} style={{ flexShrink: 0 }} />
          <span style={{ fontSize: '13px', color: C.text }}>{r.jobTitle}</span>
        </div>
      )}
      {r.company ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Building2 size={13} color={C.muted} style={{ flexShrink: 0 }} />
          <span style={{ fontSize: '13px', color: C.text }}>{r.company}</span>
        </div>
      ) : (
        <span style={{ fontSize: '12px', color: C.muted, fontStyle: 'italic' }}>No company added</span>
      )}
    </div>
  </div>
);

// ── Selection modals (education & experience) ──────────────────────
const EducationSelectionModal = ({ profileEdu, cvEducation, onClose, onSave, saving }) => {
  const [selected, setSelected] = useState(() => new Set(cvEducation || []));

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const saveData = async () => ({ cvEducation: [...selected] });

  return (
    <Modal title="Education" onClose={onClose} onSave={() => onSave(saveData)} saving={saving}>
      <p style={{ fontSize: '12px', color: C.muted, margin: '0 0 14px' }}>
        Select the education records to include in this CV. Manage your education in your Education page.
      </p>
      {(profileEdu || []).length === 0 && (
        <p style={{ fontSize: '13px', color: C.muted }}>No education records in your profile yet.</p>
      )}
      {(profileEdu || []).map((edu) => {
        const checked  = selected.has(edu.id);
        const heading  = [edu.degree, edu.fieldOfStudy].filter(Boolean).join(' — ');
        return (
          <div key={edu.id} onClick={() => toggle(edu.id)}
            style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '10px 12px', marginBottom: '6px', borderRadius: '4px', border: `1.5px solid ${checked ? C.navy : C.border}`, background: checked ? '#e8eef5' : C.white, cursor: 'pointer' }}>
            <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: checked ? C.navy : 'transparent', border: `1.5px solid ${checked ? C.navy : C.muted}`, flexShrink: 0, marginTop: '2px' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: checked ? C.navy : C.text }}>
                {heading || <em style={{ opacity: 0.5 }}>Untitled</em>}
              </div>
              {edu.institution && (
                <div style={{ fontSize: '12px', color: C.muted, marginTop: '2px' }}>{edu.institution}</div>
              )}
            </div>
          </div>
        );
      })}
    </Modal>
  );
};

const ExperienceSelectionModal = ({ profileExp, cvExperience, onClose, onSave, saving }) => {
  const [selected, setSelected] = useState(() => new Set(cvExperience || []));

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const saveData = async () => ({ cvExperience: [...selected] });

  return (
    <Modal title="Work Experience" onClose={onClose} onSave={() => onSave(saveData)} saving={saving}>
      <p style={{ fontSize: '12px', color: C.muted, margin: '0 0 14px' }}>
        Select the experience records to include in this CV. Manage your experience in your Work Experience page.
      </p>
      {(profileExp || []).length === 0 && (
        <p style={{ fontSize: '13px', color: C.muted }}>No experience records in your profile yet.</p>
      )}
      {(profileExp || []).map((exp) => {
        const checked = selected.has(exp.id);
        const startedStr = fmtExpDate(exp.startDate);
        return (
          <div key={exp.id} onClick={() => toggle(exp.id)}
            style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '10px 12px', marginBottom: '6px', borderRadius: '4px', border: `1.5px solid ${checked ? C.navy : C.border}`, background: checked ? '#e8eef5' : C.white, cursor: 'pointer' }}>
            <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: checked ? C.navy : 'transparent', border: `1.5px solid ${checked ? C.navy : C.muted}`, flexShrink: 0, marginTop: '2px' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: checked ? C.navy : C.text }}>
                {exp.jobTitle || <em style={{ opacity: 0.5 }}>Untitled position</em>}
              </div>
              <div style={{ fontSize: '12px', color: C.muted, marginTop: '2px' }}>
                {[exp.company, startedStr && `From ${startedStr}`].filter(Boolean).join(' · ')}
              </div>
            </div>
          </div>
        );
      })}
    </Modal>
  );
};

// ── Inline SVG icons for reference contact indicators ─────────────
const TelegramSVG = ({ size = 13, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.5 2.5L2 10l7 2.5M21.5 2.5L14 22l-5-9.5M21.5 2.5L9 12.5m0 0l1.5 7" />
  </svg>
);
const WhatsAppSVG = ({ size = 13, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);

// References modal — checklist from profile references
const ReferencesModal = ({ profileRefs, cvReferences, onClose, onSave, saving }) => {
  const [selected, setSelected] = useState(() => new Set(cvReferences || []));

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const saveData = async () => ({ cvReferences: [...selected] });

  return (
    <Modal title="References" onClose={onClose} onSave={() => onSave(saveData)} saving={saving}>
      <p style={{ fontSize: '12px', color: C.muted, margin: '0 0 14px' }}>
        Select the references to include in this CV. Manage your references in your Profile References page.
      </p>
      {(profileRefs || []).length === 0 && (
        <p style={{ fontSize: '13px', color: C.muted }}>No references in your profile yet. Visit the References page to add them.</p>
      )}
      {(profileRefs || []).map((ref) => {
        const checked = selected.has(ref.id);
        return (
          <div key={ref.id} onClick={() => toggle(ref.id)}
            style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '10px 12px', marginBottom: '6px', borderRadius: '4px', border: `1.5px solid ${checked ? C.navy : C.border}`, background: checked ? '#e8eef5' : C.white, cursor: 'pointer' }}>
            <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: checked ? C.navy : 'transparent', border: `1.5px solid ${checked ? C.navy : C.muted}`, flexShrink: 0, marginTop: '2px' }} />
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: checked ? C.navy : C.text }}>{ref.name}</span>
                {ref.email && <span title={ref.email} style={{ color: C.blue,  display: 'flex' }}>✉</span>}
                {ref.phone && <span title={[ref.phone, ref.phoneLabel].filter(Boolean).join(' — ')} style={{ color: C.muted, display: 'flex' }}><Phone size={13} /></span>}
                {/* hasTelegram/hasWhatsApp kept for legacy data */}
                {!ref.phoneLabel && ref.hasTelegram && <span title="Telegram"  style={{ color: C.muted, display: 'flex' }}><TelegramSVG /></span>}
                {!ref.phoneLabel && ref.hasWhatsApp && <span title="WhatsApp"  style={{ color: C.muted, display: 'flex' }}><WhatsAppSVG /></span>}
              </div>
              {(ref.jobTitle || ref.company) && (
                <div style={{ fontSize: '12px', color: C.muted, marginTop: '2px' }}>{[ref.jobTitle, ref.company].filter(Boolean).join(' · ')}</div>
              )}
            </div>
          </div>
        );
      })}
    </Modal>
  );
};

// ══════════════════════════════════════════════════════
// MAIN EDITOR
// ══════════════════════════════════════════════════════
export const SeekerCVEditor = () => {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const photoRef   = useRef();

  const [profile, setProfile]     = useState(null);
  const [cvProfile, setCVProfile] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(null);
  const [saving, setSaving]       = useState(false);

  // CV-profile-level data
  const [skillSections, setSkillSections] = useState([]);
  const [cvSkills,      setCvSkills]      = useState({});
  const [languages,     setLanguages]     = useState([]);
  const [cvLanguages,   setCvLanguages]   = useState([]);
  const [contacts,      setContacts]      = useState([]); // profile master contact list
  const [cvContacts,    setCvContacts]    = useState([]); // CV-selected contact IDs
  const [residence,     setResidence]     = useState({ street: false, city: true, country: true });
  const [cvReferences,  setCvReferences]  = useState([]); // selected reference IDs for this CV
  const [profileRefs,   setProfileRefs]   = useState([]); // full reference list from profile
  const [profileEdu,    setProfileEdu]    = useState([]); // full education list from profile
  const [cvEducation,   setCvEducation]   = useState([]); // selected education IDs for this CV
  const [profileExp,    setProfileExp]    = useState([]); // full experience list from profile
  const [cvExperience,  setCvExperience]  = useState([]); // selected experience IDs for this CV

  const loadAll = async () => {
    const [profRes, cvRes, langRes, contRes, refRes, eduRes, expRes, derivRes] = await Promise.all([
      seekerAPI.getProfile(),
      seekerAPI.getCVProfileById(id),
      seekerAPI.getLanguages(),
      seekerAPI.getContacts(),
      seekerAPI.getReferences(),
      seekerAPI.getEducation(),
      seekerAPI.getExperience(),
      seekerAPI.getSkillDerivations(),
    ]);
    const prof = profRes.data.data;
    setProfile(prof);
    const cv = cvRes.data.data;
    setCVProfile(cv);
    // Build skill sections from CustomSkillSection DB records (mirrors SeekerSkills page)
    const customSections = derivRes.data.data.customSections || [];
    const secs = customSections.map(cs => ({
      title: cs.title,
      items: (cs.skills || []).map(sk => sk.jobSeekerSkill?.skill?.displayName || sk.jobSeekerSkill?.skill?.name).filter(Boolean),
    }));
    setSkillSections(secs);
    // CV-specific selected skills (object keyed by section title)
    const cfg = cv.sectionConfig || {};
    // Migrate legacy array format → object keyed by section title (select in all sections)
    if (Array.isArray(cfg.cvSkills)) {
      const migrated = {};
      for (const cs of secs) {
        const matched = cs.items.filter(item => cfg.cvSkills.includes(item));
        if (matched.length > 0) migrated[cs.title] = matched;
      }
      setCvSkills(migrated);
    } else {
      setCvSkills(cfg.cvSkills && typeof cfg.cvSkills === 'object' ? cfg.cvSkills : {});
    }
    // Languages — map DB fields (language, proficiency) to display fields (name, level)
    const langs = (langRes.data.data || []).map(l => ({ id: l.id, name: l.language, level: l.proficiency }));
    setLanguages(langs);
    setCvLanguages(Array.isArray(cfg.cvLanguages) ? cfg.cvLanguages : []);
    // Contacts
    setContacts(contRes.data.data || []);
    setCvContacts(Array.isArray(cfg.cvContacts) ? cfg.cvContacts : []);
    setResidence(cfg.residence || { street: false, city: true, country: true });
    // References
    setProfileRefs(refRes.data.data || []);
    setCvReferences(Array.isArray(cfg.cvReferences) ? cfg.cvReferences : []);
    // Education
    setProfileEdu(eduRes.data.data || []);
    setCvEducation(Array.isArray(cfg.cvEducation) ? cfg.cvEducation : []);
    // Experience
    setProfileExp(expRes.data.data || []);
    setCvExperience(Array.isArray(cfg.cvExperience) ? cfg.cvExperience : []);
  };

  useEffect(() => { loadAll().finally(() => setLoading(false)); }, [id]);

  const closeModal = () => setModal(null);

  const handleSave = async (dataOrFn) => {
    setSaving(true);
    try {
      if (typeof dataOrFn === 'function') {
        const result = await dataOrFn();
        // Handle cvSkills / cvLanguages returned from modals
        const cfgUpdates = {};
        if (result?.cvSkills      !== undefined) cfgUpdates.cvSkills      = result.cvSkills;
        if (result?.cvLanguages   !== undefined) cfgUpdates.cvLanguages   = result.cvLanguages;
        if (result?.cvContacts    !== undefined) cfgUpdates.cvContacts    = result.cvContacts;
        if (result?.residence     !== undefined) cfgUpdates.residence     = result.residence;
        if (result?.cvReferences  !== undefined) cfgUpdates.cvReferences  = result.cvReferences;
        if (result?.cvEducation   !== undefined) cfgUpdates.cvEducation   = result.cvEducation;
        if (result?.cvExperience  !== undefined) cfgUpdates.cvExperience  = result.cvExperience;
        if (Object.keys(cfgUpdates).length > 0) {
          const cfg = { ...(cvProfile?.sectionConfig || {}), ...cfgUpdates };
          await seekerAPI.updateCVProfile(id, { sectionConfig: cfg });
          // Update local cvProfile state immediately so subsequent saves merge against fresh data
          setCVProfile(prev => ({ ...prev, sectionConfig: cfg }));
          // Update display state directly so UI reflects changes before loadAll completes
          if (cfgUpdates.cvContacts    !== undefined) setCvContacts(cfgUpdates.cvContacts);
          if (cfgUpdates.residence     !== undefined) setResidence(cfgUpdates.residence);
          if (cfgUpdates.cvSkills      !== undefined) setCvSkills(cfgUpdates.cvSkills);
          if (cfgUpdates.cvLanguages   !== undefined) setCvLanguages(cfgUpdates.cvLanguages);
          if (cfgUpdates.cvReferences  !== undefined) setCvReferences(cfgUpdates.cvReferences);
          if (cfgUpdates.cvEducation   !== undefined) setCvEducation(cfgUpdates.cvEducation);
          if (cfgUpdates.cvExperience  !== undefined) setCvExperience(cfgUpdates.cvExperience);
        }
      } else {
        await seekerAPI.updateProfile(dataOrFn);
      }
      await loadAll();
      closeModal();
      toast.success('Saved');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData(); fd.append('photo', file);
    setSaving(true);
    try { await seekerAPI.uploadProfilePhoto(fd); await loadAll(); toast.success('Photo updated'); }
    catch { toast.error('Photo upload failed'); }
    finally { setSaving(false); e.target.value = ''; }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: C.muted, fontFamily: font }}>Loading…</div>;

  const p      = profile || {};
  const skills = [];

  return (
    <div className="fade-in" style={{ fontFamily: font }}>
      {/* Page header */}
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1>{cvProfile?.title || 'CV Editor'}</h1>
          <p>Hover over any section and click to edit</p>
        </div>
        <button onClick={() => navigate('/seeker/profile')} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 14px', fontSize: '13px', fontWeight: 600, background: 'rgba(255,255,255,0.15)', color: C.white, border: '1px solid rgba(255,255,255,0.3)', borderRadius: '3px', cursor: 'pointer', fontFamily: font }}>
          <ArrowLeft size={14} /> Back to CVs
        </button>
      </div>

      {/* ── CV CARD ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', background: C.white, borderRadius: '6px', boxShadow: '0 4px 24px rgba(14,78,149,0.15)', overflow: 'visible', border: `1px solid ${C.border}`, minHeight: '900px' }}>

        {/* ── SIDEBAR ── */}
        <div style={{ background: C.navy, padding: '28px 18px', display: 'flex', flexDirection: 'column', gap: '22px', borderRadius: '6px 0 0 6px' }}>

          {/* Photo */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
            <div onClick={() => photoRef.current.click()} style={{ width: 110, height: 110, borderRadius: '50%', background: p.photoUrl ? 'transparent' : 'rgba(255,255,255,0.12)', border: '3px solid rgba(255,255,255,0.35)', overflow: 'hidden', cursor: 'pointer', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {p.photoUrl ? <img src={p.photoUrl} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 44, opacity: 0.5 }}>👤</span>}
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.opacity = 1} onMouseLeave={e => e.currentTarget.style.opacity = 0}>
                <Camera size={24} color={C.white} />
              </div>
            </div>
            <input ref={photoRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={handlePhotoChange} />
            <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.35)' }}>Click to change photo</span>
          </div>

          {/* Contacts */}
          <Editable onClick={() => setModal('contact')} style={{ padding: '6px' }}>
            <SideHead label="Contacts" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {contacts.filter(c => cvContacts.includes(c.id)).map((c, i) => (
                <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                  <span style={{ flexShrink: 0, marginTop: '1px', color: 'rgba(255,255,255,0.5)' }}>
                    {c.type === 'PHONE'
                      ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12.1 19.79 19.79 0 0 1 1.61 3.53 2 2 0 0 1 3.6 1.34h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.77a16 16 0 0 0 6.29 6.29l.95-.95a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                      : c.type === 'EMAIL'
                      ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                      : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                    }
                  </span>
                  <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.82)', lineHeight: 1.4, wordBreak: 'break-all' }}>{c.value}</span>
                </div>
              ))}
              {/* Residence — street, city, country in order */}
              {(() => {
                const parts = [
                  residence?.street  ? p.residentialAddress : null,
                  residence?.city    ? p.city               : null,
                  residence?.country ? p.country            : null,
                ].filter(Boolean);
                if (parts.length === 0) return null;
                return (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <span style={{ flexShrink: 0, marginTop: '1px' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
                    </span>
                    <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.82)', lineHeight: 1.4 }}>{parts.join(', ')}</span>
                  </div>
                );
              })()}
              {cvContacts.length === 0 && !residence?.street && !residence?.city && !residence?.country && (
                <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', margin: 0 }}>Click to add contacts…</p>
              )}
            </div>
          </Editable>

          {/* Summary */}
          <Editable onClick={() => setModal('summary')} style={{ padding: '6px' }}>
            <SideHead label="Summary" />
            <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.82)', lineHeight: 1.6, margin: 0, textAlign: 'justify' }}>
              {p.summary || <em style={{ opacity: 0.45 }}>Click to add a summary…</em>}
            </p>
          </Editable>

          {/* Languages */}
          <Editable onClick={() => setModal('languages')} style={{ padding: '6px' }}>
            <SideHead label="Languages" />
            {cvLanguages.length > 0
              ? <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {languages
                    .filter(l => cvLanguages.includes(l.name))
                    .map((l, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'rgba(255,255,255,0.7)', flexShrink: 0 }} />
                        <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>{l.name}</span>
                        {l.level && <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>— {l.level}</span>}
                      </div>
                    ))
                  }
                </div>
              : <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', margin: 0 }}>Click to add languages…</p>
            }
          </Editable>

          {/* References */}
          <Editable onClick={() => setModal('references')} style={{ padding: '6px' }}>
            <SideHead label="References" />
            {cvReferences.length > 0
              ? (() => {
                  const selected = profileRefs.filter(r => cvReferences.includes(r.id));
                  if (selected.length === 0) return <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', margin: 0 }}>Click to select references…</p>;
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {selected.map((ref, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                          <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'rgba(255,255,255,0.7)', flexShrink: 0, marginTop: '6px' }} />
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '12px', fontWeight: 700, color: 'rgba(255,255,255,0.9)' }}>{ref.name}</span>
                              {ref.email && <span title={ref.email} style={{ color: 'rgba(255,255,255,0.55)', lineHeight: 1 }}>✉</span>}
                              {ref.phone && <span title={[ref.phone, ref.phoneLabel].filter(Boolean).join(' — ')} style={{ color: 'rgba(255,255,255,0.55)', display: 'flex' }}><Phone size={12} color="rgba(255,255,255,0.55)" /></span>}
                              {/* hasTelegram/hasWhatsApp kept for legacy data */}
                              {!ref.phoneLabel && ref.hasTelegram && <span title="Telegram"  style={{ color: 'rgba(255,255,255,0.55)', display: 'flex' }}><TelegramSVG color="rgba(255,255,255,0.55)" /></span>}
                              {!ref.phoneLabel && ref.hasWhatsApp && <span title="WhatsApp"  style={{ color: 'rgba(255,255,255,0.55)', display: 'flex' }}><WhatsAppSVG color="rgba(255,255,255,0.55)" /></span>}
                            </div>
                            {(ref.jobTitle || ref.company) && (
                              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '1px' }}>
                                {[ref.jobTitle, ref.company].filter(Boolean).join(', ')}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()
              : <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', margin: 0 }}>Click to select references…</p>
            }
          </Editable>

        </div>

        {/* ── MAIN PANEL ── */}
        <div style={{ padding: '32px 32px 40px', display: 'flex', flexDirection: 'column', gap: '26px' }}>

          {/* Name (read-only) + Headline (editable) */}
          <div>
            <h1 style={{ fontSize: '40px', fontWeight: 800, color: C.text, margin: '0 0 4px', lineHeight: 1.1, fontFamily: font }}>
              {p.firstName || 'First'} {p.lastName || 'Last'}
            </h1>
            <Editable onClick={() => setModal('headline')} style={{ display: 'inline-block' }}>
              <p style={{ fontSize: '15px', fontStyle: 'italic', color: C.blue, margin: 0, fontWeight: 500, minWidth: '200px' }}>
                {p.headline || <em style={{ opacity: 0.4 }}>Click to add a headline…</em>}
              </p>
            </Editable>
          </div>

          {/* Skills & Knowledge — CV-selected skills, per section */}
          <div>
            <MainHead label="Skills & Knowledge" />
            <Editable onClick={() => setModal('skills')}>
              {Object.values(cvSkills).some(arr => arr.length > 0)
                ? <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {skillSections.map((sec, i) => {
                      const sectionSkills = (cvSkills[sec.title] || []).filter(item => sec.items.includes(item));
                      if (sectionSkills.length === 0) return null;
                      return (
                        <div key={i}>
                          {sec.title && <div style={{ fontSize: '11px', fontWeight: 700, color: C.blue, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '5px' }}>{sec.title}</div>}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', paddingLeft: '6px' }}>
                            {sectionSkills.map((skill, j) => (
                              <div key={j} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: C.navy, flexShrink: 0 }} />
                                <span style={{ fontSize: '13px', color: C.text }}>{skill}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                : <p style={{ fontSize: '13px', color: C.muted, margin: 0 }}>Click to select skills for this CV…</p>
              }
            </Editable>
          </div>

          {/* Education */}
          <div>
            <MainHead label="Education" />
            <Editable onClick={() => setModal('education')}>
              {cvEducation.length > 0 && profileEdu.some(e => cvEducation.includes(e.id))
                ? <div className="form-row" style={{ flexWrap: 'wrap' }}>
                    {profileEdu.filter(e => cvEducation.includes(e.id)).map(e => (
                      <CVEduCard key={e.id} edu={e} />
                    ))}
                  </div>
                : <p style={{ fontSize: '13px', color: C.muted, margin: 0 }}>Click to select education records for this CV…</p>
              }
            </Editable>
          </div>

          {/* Experience */}
          <div>
            <MainHead label="Experience" />
            <Editable onClick={() => setModal('experience')}>
              {cvExperience.length > 0 && profileExp.some(e => cvExperience.includes(e.id))
                ? <div className="form-row" style={{ flexWrap: 'wrap' }}>
                    {profileExp.filter(e => cvExperience.includes(e.id)).map(e => (
                      <CVExpCard key={e.id} exp={e} />
                    ))}
                  </div>
                : <p style={{ fontSize: '13px', color: C.muted, margin: 0 }}>Click to select experience records for this CV…</p>
              }
            </Editable>
          </div>


        </div>
      </div>

      {/* ── MODALS ── */}
      {modal === 'headline'    && <HeadlineModal    p={p}           onClose={closeModal} onSave={handleSave} saving={saving} />}
      {modal === 'contact'     && <ContactModal     contacts={contacts} cvContacts={cvContacts} residence={residence} onClose={closeModal} onSave={handleSave} saving={saving} />}
      {modal === 'summary'     && <SummaryModal     p={p}           onClose={closeModal} onSave={handleSave} saving={saving} />}
      {modal === 'personal'    && <PersonalModal    p={p}           onClose={closeModal} onSave={handleSave} saving={saving} />}
      {modal === 'skills'      && <SkillsModal      skillSections={skillSections} cvSkills={cvSkills} onClose={closeModal} onSave={handleSave} saving={saving} />}
      {modal === 'languages'   && <LanguagesModal   languages={languages} cvLanguages={cvLanguages} onClose={closeModal} onSave={handleSave} saving={saving} />}
      {modal === 'references'  && <ReferencesModal          profileRefs={profileRefs} cvReferences={cvReferences} onClose={closeModal} onSave={handleSave} saving={saving} />}
      {modal === 'education'   && <EducationSelectionModal   profileEdu={profileEdu}   cvEducation={cvEducation}   onClose={closeModal} onSave={handleSave} saving={saving} />}
      {modal === 'experience'  && <ExperienceSelectionModal  profileExp={profileExp}   cvExperience={cvExperience} onClose={closeModal} onSave={handleSave} saving={saving} />}
    </div>
  );
};
